import * as Sentry from '@sentry/react';
import { compareVersions } from 'compare-versions';
import { noop } from 'lodash';
import { openLoginPopup } from '@meitu/account-login-popup';
import { LoginMethod, MeituAccount, STORAGE_KEY_LOGIN_BY_WEB, ThirdPartyPlatform } from '@meitu/account-web';
import { getMeituAccountEncryptedToken, loginMeituAccount, LoginMeituAccountParams } from '@meitu/hogger';
import { bridge, clientInfo } from '@meitu/pc-bridge';
import SaasSubscription, {
    CloseFromEnum,
    EnvEnum,
    EventNameEnum,
    ICommonParams,
    IParams,
    ThemeType,
} from '@meitu/saas-subscription-popup';
import { getLocalIdentity, setLocalIdentity } from '@meitu/xdesign-identity';
import { gaEventTracker, getCountryCode, popupManager } from '@meitu/xdesign-utils';
import {
    clearTeamInfo,
    disableIdentityChangeListener,
    enableIdentityChangeListener,
    fetchTeamInfo,
    openIdentitySwitchPopupAfterLogin,
    setLocalUid,
    TypeOpenVipTeamModal,
} from '@meitu/xdesign-widgets';
import { getGclid, getGoogleCookieId } from '@meitu/xdesign-widgets/dist/collectCookie';
import { CLOSE_BUSINESS_MESSAGE_MODAL } from '@meitu/xdesign-widgets/dist/floatHelpV2/constants';
import { getJumpQuery } from '@meitu/xdesign-widgets/dist/overseasAccessModal/jumpQuery';
import eventBus from '@meitu/xdesign-widgets/dist/utils/eventBus';
import { getLocaleStore } from '@/i18n';
import { isIntl, REACT_APP_ENV, REACT_APP_GOOGLE_CLIENT_ID } from '@/react-process-env';
import { fetchUserInfo } from '@/services/user';
import getStore from '@/store';
import { APP_ID, Entrance, SubscribeCategoryGroupCode } from '@/types';
import { getAbInfo, isRetentionTest } from './ab-test';
import { getAppKey } from './app-key';
import { getAccessToken, getClientId, getPCToken, isPcPlatform } from './auth';
import { checkImageEditor, isMobile } from './common';
import { getPrefix, isBetaEnv, isDevelopEnv, isPreEnv } from './env';
import { waitUntilGetGid } from './gid';
import { getOperateSource, statConfig } from './mtstat';
import * as native from './native';
import { checkIsInSdk, getQuery } from './query';
import { getCurrentScene, getMaterialTrackObj, Scene } from './track';

export const getFirstUrl = () => {
    const query = getQuery();
    return decodeURIComponent(query.first_url || '') || window.sessionStorage.getItem('first_url') || '';
};

let accessToken = '';
let processingLogin: (token: string) => void = () => {};

const getEnvValue = (env?: string) => {
    // 1 test, 0 线上（默认）, 2 beta
    let value;
    switch (env) {
        case 'release':
            value = 0;
            break;
        case 'beta':
            value = 2;
            break;
        default:
            value = 1;
    }

    return value;
};

export const callback = `${window.location.protocol}//${window.location.host}/${getPrefix()}callback.html`;

// 初始化配置
gaEventTracker.setConfig({
    env: REACT_APP_ENV as any,
});

const openAccount = async (showClose = false, onClose?: (userClose?: boolean) => void, loginMethod?: LoginMethod) => {
    //第三方帐号登录，退出后使用页面跳转涉及帐号内部状态同步，须确保帐号内部登录态清除
    await window.account.isLogin();

    // 登录弹窗打开标记
    window.account.loginShow = true;

    // 海外需要一进入页面就直接弹谷歌一键登录
    if (isIntl && loginMethod === LoginMethod.GoogleOneTap) {
        openLoginPopup({
            // ...登录弹窗配置选项，详见：LoginPopupProps
            force: true,
            useLoginMethod: loginMethod,
            googleClientId: REACT_APP_GOOGLE_CLIENT_ID,
            onClose(userClose?: boolean) {
                console.log('Popup Login onClose ');
                delete window.account?.loginShow;
                onClose?.(userClose);
            },
            onLoginSuccess: () => {
                console.log('Popup Login success ');
            },
            onLogoutError: () => {
                console.log('Popup Login error');
            },
        });
    } else {
        // 原弹窗框逻辑
        openLoginPopup({
            title: (text: string) => {
                // 海外弹窗文案
                if (isIntl) {
                    return (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <div
                                style={{
                                    color: 'var(--content-primary, #0f1729)',
                                    textAlign: 'center',
                                    fontSize: '24px',
                                    fontStyle: 'normal',
                                    fontWeight: 700,
                                    lineHeight: '32px',
                                    width: '220px',
                                    whiteSpace: 'pre-wrap',
                                }}
                            >
                                Log in to unlock your free trial
                            </div>
                            <div
                                style={{
                                    color: 'var(--content-secondary, #616366)',
                                    textAlign: 'center',
                                    fontFamily: 'Inter',
                                    fontSize: '12px',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    lineHeight: 'normal',
                                    marginTop: '8px',
                                }}
                            >
                                Start creating with Zawa today
                            </div>
                        </div>
                    );
                } else {
                    return (
                        <div
                            style={{
                                color: 'var(--content-primary, #0f1729)',
                                textAlign: 'center',
                                fontSize: '24px',
                                fontStyle: 'normal',
                                fontWeight: 700,
                                lineHeight: '32px',
                            }}
                        >
                            {text}
                        </div>
                    );
                }
            },
            // ...登录弹窗配置选项，详见：LoginPopupProps
            autoDetectAndSetDarkTheme: false,
            theme: 'v4',
            containerStyle: {
                minHeight: 473,
            },
            force: true,
            useLoginMethod: isIntl ? LoginMethod.ThirdPartyAccounts : '',
            showMeituXiuXiuQRCode: false,
            thirdPartyAccounts: isIntl
                ? [
                      // ThirdPartyPlatform.Facebook,
                      // ThirdPartyPlatform.Apple,
                      ThirdPartyPlatform.Google,
                      ThirdPartyPlatform.Email,
                  ]
                : isMobile()
                  ? [ThirdPartyPlatform.Meitu]
                  : [
                        ThirdPartyPlatform.CustomQRCode,
                        ThirdPartyPlatform.Meitu,
                        ThirdPartyPlatform.Weibo,
                        ThirdPartyPlatform.WeChat,
                        ThirdPartyPlatform.QQ,
                    ],
            borderRadius: '14px',
            // 隐藏关闭按钮
            showCloseButton: showClose,
            closeable: true,
            switch: true, // 切换账号，会先清除登录状态
            leftSide() {
                return isIntl
                    ? '<img style="display: block; width: 100%; height: 100%;" src="https://x-design-release.stariidata.com/poster/188f73dbe540625ea0ee170912b11dba.jpg?imageView2/2/w/650/format/jpg/q/60/ignore-error/1" />'
                    : `<video poster="https://public.static.meitudata.com/xiuxiu-pc/x-design/videoframe_1193.jpg" src="https://public.static.meitudata.com/xiuxiu-pc/x-design/login-banner2.webm" autoplay muted loop playsinline style="width: 334px; height: 100%; min-height: 509px; border-radius: 24px 0 0 24px; object-fit: cover; object-position: center center; display: block;"></video>`;
            },
            onClose(userClose?: boolean) {
                delete window.account?.loginShow;
                onClose?.(userClose);
                // 关闭弹窗后的自定义处理，略...
            },
        });
    }
};

export const initAccount = async () => {
    const gid = await waitUntilGetGid();

    window.account = MeituAccount({
        env: getEnvValue(REACT_APP_ENV), // 指定环境 0 正式环境（默认）、1 Preview 测试环境、2、Beta 拟真测试环境
        gid, // 可选；自定义 gid
        clientId: getClientId(),
        theme: isIntl ? 'v4' : 'mtxx',
        clientLanguage: isIntl ? 'en' : 'zh-Hans',
        clientConfigs: {
            enableLoginHistory: !isIntl,
            needPhone: false,
            enableWeChatOfficialAccount: true, // 开启关注公众号登录
            googleClientId: REACT_APP_GOOGLE_CLIENT_ID,
            useGoogleAutoSelect: false, // 我们退出登录会刷新页面，开启谷歌自动登录后，刷新页面就还会再自动登录，所以需要关闭
            emailLoginMethod: 'verify_code', // 启用邮箱验证码登录，主要给 VMake 过来的用户使用
            customQRCodeLogin: {
                name: '美图设计室',
                icon: 'https://xiuxiu-pro-new.meitudata.com/poster/5ce9f95aa32b36358438f16afe666485.png',
                texts: {
                    title: '美图设计室扫码登录',
                    appName: '美图设计室',
                    scanGuide: '使用我的-右上方“扫一扫”登录',
                },
                statisticPlatform: 'designkit',
            },
        },
        syncDomains: true, // 可选；是否开启跨域名（非 *.meitu.com 域）同步登录和退出状态（HTTPS 域名才有效，目前只支持 Chrome 系浏览器，不支持 Safari 和火狐浏览器）
        // onNeedLogin(error) {},
        onLogining() {},
        // onLoginError(e) {},

        onLoginSuccess: async (result: any, isRegister) => {
            console.log('onLoginSuccess: result ======>', result);

            const status = await openIdentitySwitchPopupAfterLogin(result.uid, result.accessToken);

            if (!status) return;

            window.localStorage.setItem('access_token', result.accessToken);
            // 登录成功
            processingLogin(result.accessToken);

            setLocalUid(result.uid);
            // TODO: 增加等待获取用户信息成功后，再执行登录成功回调
            getUserInfo(() => handleAutoPopupModal({ type: 'login' }));
            // 弹窗登录后设置埋点的uid
            window.mtstat?.login(result.uid);

            // sentry设置用户信息
            if (isIntl) {
                Sentry.setUser({
                    id: `${result.uid}`,
                });
            }
            if (isIntl) {
                gaEventTracker.sendLoginGaTrack(isRegister, result.platform, statConfig.module_id);
            }

            // 登录成功回调里，强制设置为个人身份
            if (!isIntl) {
                setLocalIdentity(0, 0);
            }
        },
        // onLogoutError(e) {},
        onLogoutSuccess() {},
        syncDomainsOptions: {
            onComplete(opType) {
                if (opType === 3) {
                    window.location.reload();
                    // 清空主动弹出订阅弹窗次数数据
                    popupManager.logout();
                }
            },
        },
    });
};

// const SEND_LOGIN_TIMES = '__sendLoginTimer__';
// 标识是否打开内嵌的登录弹窗
export const SEND_LOGIN_STATUS = '__sendLoginStatus__';
export const openPCLogin = () => {
    //存在多处调用login，需要控制向客户端发送指令的数量，退出登录还存在刷新页面重复发送，所以基于localstorage来做
    // const timer =
    //     new Date().getTime() -
    //     parseInt(localStorage.getItem(SEND_LOGIN_TIMES) || '0');
    // if (timer > 8 * 1000) {
    //     // 8秒内不重复弹登录框
    //     localStorage.setItem(SEND_LOGIN_TIMES, new Date().getTime().toString());
    //     native.relogin && native.relogin(1); // relogin(1) 关闭登录弹窗时跳转图片编辑， 0：不做处理
    // }
    const hasOpenLoginModal = parseInt(localStorage.getItem(SEND_LOGIN_STATUS) || '0');
    if (hasOpenLoginModal) return;

    localStorage.setItem(SEND_LOGIN_STATUS, '1');
    if (native.relogin) {
        native.relogin(1); // relogin(1) 关闭登录弹窗时跳转图片编辑， 0：不做处理
    }
};

// 未登录态时，打开登录弹窗
export const jumpToLogin = async (
    showClose?: boolean,
    onClose?: (userClose?: boolean) => void,
    loginMethod?: LoginMethod,
): Promise<string> => {
    console.log('jumpToLogin');
    showClose = showClose ?? checkImageEditor();
    if (isPcPlatform()) {
        openPCLogin();
        const token = await getPCToken();
        return token;
    }

    if (isIntl) {
        gaEventTracker.setEntrance(loginMethod === LoginMethod.GoogleOneTap ? 'quick' : 'page_enter');
    }

    if (!window.account) {
        await initAccount();
    }

    // 登录时收起在线咨询弹窗
    eventBus.emit(CLOSE_BUSINESS_MESSAGE_MODAL);

    // 打开登录弹窗
    await openAccount(showClose, onClose, loginMethod);

    // 等待登录结果
    return await new Promise((resolve) => (processingLogin = resolve));
};

// 参数生成
export const genParams = (params: ICommonParams, isPcPlatform: boolean, webScene?: string): ICommonParams => {
    const abInfo = getAbInfo();
    if (abInfo && !isIntl) {
        params['ab_info'] = abInfo;
    }

    const qsParams = getQuery();

    params.template_id = `${getStore().getState().templateModel.id}`;
    const { paid_material } = getMaterialTrackObj();
    params['vip_type'] = paid_material;
    if (isPcPlatform) {
        //客户端统计参数跟web非一一对应
        // 在内嵌时进行参数转换

        let { entrance } = params;
        if (entrance === 'open') {
            entrance = 'vip';
        }
        params.first_source = 'hbsj';
        params['source'] = entrance;

        return {
            ...params,
            bigData: {
                ...params,
            },
        };
    } else {
        if (webScene) {
            params['source'] = params.source || webScene;
            params['module_id'] = webScene;
        }

        params['user_source'] = qsParams.from;

        // 跨域名打开订阅，需要透传channel和first_url
        if (qsParams.channel || window.sessionStorage.getItem('channel')) {
            params['channel'] = qsParams.channel || window.sessionStorage.getItem('channel');
            params['first_url'] = getFirstUrl();
        }
        const matrix_channel = qsParams.matrix_channel || window.sessionStorage.getItem('matrix_channel');
        if (matrix_channel) {
            params['matrix_channel'] = matrix_channel;
        }
        if (qsParams.origin_func) {
            params['origin_func'] = qsParams.origin_func;
        }
    }

    return params;
};

let vipModalCloseCallback: (from?: CloseFromEnum) => void = noop;
let vipModalPayCallback: () => void = noop;

/**
 * 初始化订阅弹窗组件
 */
const initVipModal = async (initParams: IParams) => {
    if (!initParams.commonParams) {
        initParams.commonParams = {};
    }
    const jumpQuery = getJumpQuery();
    initParams.commonParams.original_referrer = jumpQuery.original_referrer || '';
    initParams.commonParams.came_from = jumpQuery.came_from || '';
    initParams.commonParams.operate_source = getOperateSource();
    const instanceVipModal = new SaasSubscription(initParams);
    instanceVipModal
        .on(EventNameEnum.SUCCESS, () => {
            // 购买成功更新接口
            getUserInfo();
            // 更新团队状态
            fetchTeamInfo();
            vipModalPayCallback();
        })
        .on(EventNameEnum.LOGININVALID, () => {
            // 登录态失效
            // handleLogout();
            window.location.reload();
        })
        .on(EventNameEnum.CLOSE, (from) => {
            vipModalCloseCallback(from);
        });

    window.instanceVipModal = instanceVipModal;
};

/**
 * 初始化海外订阅弹窗
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const openVipIntlModal = async (params: any = {}) => {
    const { SubscribeEnv, openSubscribePopup } = await import('@meitu/subscribe-ui-intl');
    await import('@meitu/subscribe-ui-intl/dist/style.css');

    const userInfo = getStore().getActions().userInfoModel.getUserInfo();
    const env =
        REACT_APP_ENV === 'release'
            ? SubscribeEnv.Release
            : REACT_APP_ENV === 'beta'
              ? SubscribeEnv.Beta
              : SubscribeEnv.Pre;

    let categoryGroupCode = '';

    if (params.categoryGroupCode) {
        categoryGroupCode = params.categoryGroupCode;
    } else if (!params.subscribe_type || params.subscribe_type === 'subscribe') {
        // 普通会员购买场景，默认都打开会员购买tab
        categoryGroupCode = SubscribeCategoryGroupCode.PayDefaultVip;
    } else {
        // 美叶消耗场景：如果是非会员则默认打开会员购买tab，如果是会员则打开
        categoryGroupCode = !userInfo.is_vip
            ? SubscribeCategoryGroupCode.PayDefaultVip
            : SubscribeCategoryGroupCode.PayDefaultCredit;
    }

    // 剔除一些冗余字段
    delete params.categoryGroupCode;
    delete params.subscribe_type;

    if (!params.touch_type) {
        params.touch_type = 4;
    }
    const [gclid, gg_client_id] = await Promise.all([getGclid(), getGoogleCookieId()]);
    params.gclid = gclid || '';
    params.gg_client_id = gg_client_id || '';
    params.user_type = userInfo?.is_vip ? 1 : 0;
    params.operate_source = 'web';
    params.gid = params.device_id = await waitUntilGetGid();
    params.country_code = await getCountryCode();
    const qsParams = getQuery();
    params.first_url = getFirstUrl();
    params.channel = qsParams.channel || window.sessionStorage.getItem('channel') || '';

    const instanceModal = openSubscribePopup({
        config: {
            env,
            accessToken: getAccessToken() || '',
            clientInfo: {
                appId: APP_ID,
                vipGroup: 'group_design',
                categoryGroupCode,
                platform: 4,
                theme: 'light',

                language: getLocaleStore().locale as any,
                transferData: {
                    baseData: {},
                    businessData: {
                        ...params,
                        // app_key: getAppKey()
                    },
                    bigData: {
                        ...params,
                        app_key: getAppKey(),
                    },
                },
            },
        },
        paySuccessUrl: `https://${window.location.hostname}/payment/success`,
        async onTradeFinish(tradeProgress) {
            instanceModal.close();
            // 2025-04-15 产品要求取消该埋点
            // gaEventTracker.sendPurchaseGaTrack(tradeProgress, !!userInfo.is_vip, statConfig.module_id);
        },
        onTradeConfirm() {
            setTimeout(() => {
                getUserInfo();
            }, 100);
        },
        onLogin() {
            instanceModal.close();
            jumpToLogin();
        },
    });
};

// 退出登录回调
export const handleLogout = async () => {
    // 清除埋点中的uid
    window.mtstat?.logout();
    if (isPcPlatform()) {
        if (native && native.logout) {
            native.logout();
        }
    }

    window.sessionStorage.removeItem(STORAGE_KEY_LOGIN_BY_WEB);
    if (!window.account) {
        await initAccount();
    }

    let options = {};
    if (accessToken) {
        options = {
            headers: {
                'Access-Token': accessToken,
            },
        };
    }
    window.account?.logout({}, options);

    // 清除个人信息
    getStore().getActions().userInfoModel.destroy();
    // 清除团队信息
    clearTeamInfo();
    // 关闭身份监听
    disableIdentityChangeListener();
};

// 打开VIP会员弹窗
export const openVipModal: TypeOpenVipTeamModal = async (inParams = {}, closeCallBack = noop, payCallback = noop) => {
    vipModalCloseCallback = closeCallBack;
    vipModalPayCallback = payCallback;

    const isPcPlatformTag = isPcPlatform();
    const currentScene = getCurrentScene();
    const params = genParams(inParams, isPcPlatformTag, currentScene);
    if (isPcPlatformTag) {
        const version = await bridge.getVersion();
        const isPc = clientInfo.isPC;
        const isMac = clientInfo.isMac;
        // 相等时返回0，小于返回1，大于返回-1
        if ((isMac && compareVersions('1.7.65', version) <= 0) || (isPc && compareVersions('7.0.9.9', version) <= 0)) {
            return bridge.openSubscribeWithParams(params);
        } else {
            return bridge.openSubscribe();
        }
    } else {
        // 订阅团队id
        const localIdentity = getLocalIdentity();
        // 打开团队订阅，调整 app_key
        if (localIdentity?.oid) {
            params.oid = localIdentity?.oid || 0;
            params.groupId = localIdentity?.groupId || 0;
            params.purchase_scene = 'normal';
            // params.app_key = getAppKey();
        }

        if (params?.oid) {
            // 如果场景透传了oid，则需要实时根据最新的oid去透传对应的app_key
            params.app_key = getAppKey(params.oid);
        }

        // 海外订阅
        if (isIntl) {
            openVipIntlModal(params);
            gaEventTracker.setEntrance(params.entrance || 'other');
            return;
        }
        if (!window.instanceVipModal) {
            const query = getQuery();
            const initVipModalParams: IParams = {
                env: isDevelopEnv()
                    ? EnvEnum.LOCAL
                    : isPreEnv()
                      ? EnvEnum.PRE
                      : isBetaEnv()
                        ? EnvEnum.BETA
                        : EnvEnum.RELEASE,
                theme: getStore().getState().editorModel.theme as ThemeType,
                commonParams: {
                    module_id: currentScene,
                    source: currentScene,
                    second_source: params.second_source || '',
                    third_source: params?.third_source || '',
                    client_id: getClientId(),
                    app_key: getAppKey(),
                    callback_origin: window.location.origin,
                    origin_func: params.origin_func,
                    user_source: params.user_source || '',
                    hideLowPriceTrialCopywriting: currentScene === Scene.AiPPT ? 1 : 0,
                    operate_source: 'web',
                    first_url: getFirstUrl(),
                    channel: query.channel || window.sessionStorage.getItem('channel') || '',
                },
            };
            // console.log('getAppKey => initVipModalParams', {
            //     ...initVipModalParams
            // });
            await initVipModal(initVipModalParams);
        } else {
            window.instanceVipModal.setTheme(getStore().getState().editorModel.theme);
        }
        // 打开VIP弹窗
        console.log('params', { openVipModalParams: params });
        window.instanceVipModal?.open(params, {
            shouldRetention: isRetentionTest(),
            onTrack: window._stat_track,
        });
    }
};

export const UPDATE_USER_INFO = 'UPDATE_USER_INFO';
export const UPDATE_PRICE = 'UPDATE_PRICE';

/**
 * 获取用户信息
 */

export const getUserInfo = async (cb?: Function) => {
    // 旧官网用户信息
    const [res] = (await Promise.all([fetchUserInfo({})])) as any;

    if (res?.id) {
        window.mtstat?.login(res.id);
        setLocalUid(res.id);
        if (res.access_token) {
            window.localStorage.setItem('access_token', res.access_token);
            accessToken = res.access_token;
        }
        getStore().getActions().userInfoModel.setUserInfo(res);
        getStore().getActions().meidouModel.setMeidouInfo(res);
        getStore().getActions().cutoutModel.setCutoutInfo(res.cutoutInfo);
        if (isPcPlatform()) {
            localStorage.setItem(SEND_LOGIN_STATUS, '0');
        }

        // 获取用户信息接口返回成功之后需要手动开启身份监听
        enableIdentityChangeListener();
    } else {
        getStore().getActions().userInfoModel.destroy();
    }
    getStore().getActions().initModel.setHasGetInfo(true);

    cb?.(res);
    return res;
};

export const handleUserInfo = async () => {
    const prefix = REACT_APP_ENV === 'beta' ? 'beta' : REACT_APP_ENV === 'release' ? '' : 'pre';
    const url = `${
        window.location.protocol
    }//${prefix}account.meitu.com/#!/?client_id=${getClientId()}&client_callback=${callback}`;
    if (isPcPlatform()) {
        native.openBrowser(url);
    } else {
        window.open(url);
    }
};

/**
 * 初始化谷歌一键登录
 *
 * 1. 不是 SDK
 * 2. 没有登录信息
 * 3. 必须是海外环境
 */
export const initGoogleOneTapLogin = async () => {
    const userInfo = getStore().getActions().userInfoModel.getUserInfo();
    const isInSDK = checkIsInSdk();
    // 海外图片编辑需要谷歌一键登录
    if (!isInSDK && !userInfo && isIntl && checkImageEditor()) {
        await jumpToLogin(undefined, undefined, LoginMethod.GoogleOneTap);
    }
};

/**
 * 处理非会员用户主动弹出订阅弹窗
 *
 * @param params - 订阅弹窗参数
 */
export const handleAutoPopupModal = async (params: { type: 'login' | 'download' }) => {
    const userInfo = getStore().getActions().userInfoModel.getUserInfo();
    const isVip = userInfo?.is_vip;
    if (!isIntl || isVip) return;
    const { type } = params;
    let entrance = Entrance.AutoLogin;
    if (type === 'download') {
        entrance = Entrance.AutoSave;
    }
    gaEventTracker.setEntrance(entrance);

    popupManager.handlePopup(type, () => {
        openVipModal({
            entrance,
            // source: getFrom(),
            vip_type: '',
        });
    });
};

export const appLogin = async (options: LoginMeituAccountParams = { type: 'half' }): Promise<void> => {
    try {
        await loginMeituAccount(options);
        const [res] = await getMeituAccountEncryptedToken();
        window.localStorage.setItem('access_token', res.encryptedToken);
        await getUserInfo();
        return Promise.resolve();
    } catch (e) {
        console.log('appLogin error', e);
        return Promise.reject('[用户取消登录]');
    }
};
